import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useEffect, useRef, useState } from "react";
import { getPracticeCategories } from "../constants/practiceAreas";
import API_BASE_URL from "../config";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

import {
  FiHome,
  FiSearch,
  FiUser,
  FiChevronDown,
  FiInfo,
  FiPhone,
  FiLogOut,
  FiMenu,
  FiX,
  FiUserCheck,
  FiUserPlus,
  FiDollarSign
} from "react-icons/fi";

import { FaPoundSign } from "react-icons/fa";

import "../styles/Header.css";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  /* ======================
     STATE
  ====================== */
  const [practiceCategories, setPracticeCategories] = useState({});
  const [open, setOpen] = useState(false);
  const [showFindMenu, setShowFindMenu] = useState(false);
  const [showWhoWeAre, setShowWhoWeAre] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const dropdownRef = useRef(null);
  const findMenuRef = useRef(null);
  const whoWeAreRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const [siteSettings, setSiteSettings] = useState({
    brandName: "",
    logo: null,
    logoContentType: null
  });

  /* ======================
     PENDING BOOKING ALERT
  ====================== */
  useEffect(() => {
    if (!user) return;

    const pending = localStorage.getItem("pendingBooking");
    if (!pending) return;

    if (sessionStorage.getItem("draftAlertShown") === "true") return;

    const currentPath = window.location.pathname;
    if (currentPath.includes("/book-appointment")) return;

    const data = JSON.parse(pending);

    const timer = setTimeout(() => {
      Swal.fire({
        title: "Pending Booking",
        text: "You have an unfinished booking. Do you want to continue?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Continue",
        cancelButtonText: "Cancel",
        allowOutsideClick: false
      }).then((result) => {
        sessionStorage.setItem("draftAlertShown", "true");

        if (result.isConfirmed) {
          navigate(`/book-appointment/${data.solicitorId}`, {
            state: { fromHeaderDraft: true }
          });
        }
      });
    }, 600);

    return () => clearTimeout(timer);
  }, [navigate, user]);

  /* ======================
     DRAFT EXPIRY
  ====================== */
  useEffect(() => {
    const saved = localStorage.getItem("pendingBooking");
    if (!saved) return;

    const parsed = JSON.parse(saved);
    const now = Date.now();
    const draftAge = now - parsed.timestamp;
    const DRAFT_EXPIRY_TIME = 24 * 60 * 60 * 1000;

    if (draftAge > DRAFT_EXPIRY_TIME) {
      localStorage.removeItem("pendingBooking");
      sessionStorage.removeItem("draftAlertShown");
    }
  }, []);

  /* ======================
     LOAD PRACTICE AREAS
  ====================== */
  useEffect(() => {
    const loadPracticeAreas = async () => {
      try {
        const data = await getPracticeCategories();
        setPracticeCategories(data || {});
      } catch (err) {
        console.error("Failed to load practice categories", err);
      }
    };
    loadPracticeAreas();
  }, []);

  /* ======================
     LOAD SITE SETTINGS
  ====================== */
  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/site-settings`);
        const data = await res.json();
        if (data) setSiteSettings(data);
      } catch (err) {
        console.error("Failed to load site settings in header", err);
      }
    };
    fetchSiteSettings();
  }, []);

  /* ======================
     HANDLERS
  ====================== */
  const handleLogout = () => {
    logout();
    setOpen(false);
    setIsMenuOpen(false);
    navigate("/login");
    sessionStorage.clear();
    localStorage.clear();
  };

  const goToDashboard = () => {
    if (user.role === "SOLICITOR") navigate("/solicitor/dashboard");
    if (user.role === "USER") navigate("/user/dashboard");
    setIsMenuOpen(false);
  };

  const handlePracticeClick = (category, area) => {
    setShowFindMenu(false);
    setIsMenuOpen(false);
    navigate(
      `/search?category=${encodeURIComponent(category)}&area=${encodeURIComponent(area)}`
    );
  };

  const handleNavClick = () => {
    setIsMenuOpen(false);
    setShowWhoWeAre(false);
    setShowFindMenu(false);
  };

  /* ======================
     OUTSIDE CLICK
  ====================== */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
      if (findMenuRef.current && !findMenuRef.current.contains(e.target)) {
        setShowFindMenu(false);
      }
      if (whoWeAreRef.current && !whoWeAreRef.current.contains(e.target)) {
        setShowWhoWeAre(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ======================
     UI
  ====================== */
  return (
    <header className="header">
      <Link to="/" className="logoWrapper">
        {siteSettings.logo && siteSettings.logoContentType && (
          <img
            src={`data:${siteSettings.logoContentType};base64,${siteSettings.logo}`}
            alt="Logo"
            className="header-logo-img"
          />
        )}
        <span className="logoText">
          {siteSettings.brandName.toUpperCase() || ""}
        </span>
      </Link>

      {/* MOBILE TOGGLE */}
      <button
        className="mobile-menu-toggle"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
      >
        {isMenuOpen ? <FiX /> : <FiMenu />}
      </button>

      <nav className={`nav ${isMenuOpen ? "nav-open" : ""}`} ref={mobileMenuRef}>
        <Link to="/" className="navLink" onClick={handleNavClick}>
          <FiHome /> <span>Home</span>
        </Link>

        <Link to="/search" className="navLink" onClick={handleNavClick}>
          <FiSearch /> <span>Search</span>
        </Link>

        {/* FIND SOLICITOR */}
        <div
          className="navLink navDropdown findDropdown"
          ref={findMenuRef}
          onClick={() => setShowFindMenu(!showFindMenu)}
        >
          <FiUserCheck />
          <span>Find the Solicitor</span>
          <FiChevronDown className={`chevron ${showFindMenu ? "rotate" : ""}`} />

          {showFindMenu && (
            <div className="mega-menu">
              {Object.entries(practiceCategories).map(([category, areas]) => (
                <div key={category} className="menu-column">
                  <h4>{category}</h4>
                  {areas.map((area) => (
                    <div
                      key={area}
                      className="menu-item"
                      onClick={() => handlePracticeClick(category, area)}
                    >
                      {area}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* WHO WE ARE */}
        <div
          className="navLink navDropdown"
          ref={whoWeAreRef}
          onClick={() => setShowWhoWeAre(!showWhoWeAre)}
        >
          <FiInfo />
          <span>Who We Are</span>
          <FiChevronDown className={`chevron ${showWhoWeAre ? "rotate" : ""}`} />

          {showWhoWeAre && (
            <div className="dropdown-menu">
              <Link to="/about" className="dropdownItem" onClick={handleNavClick}>
                <FiInfo /> About Us
              </Link>

              <Link to="/contact" className="dropdownItem" onClick={handleNavClick}>
                <FiPhone /> Contact
              </Link>
            </div>
          )}
        </div>

        {/* PRICING */}
        {!user && (
          <Link to="/subscription" className="navLink" onClick={handleNavClick}>
            <FaPoundSign /> <span>Pricing</span>
          </Link>
        )}

        {/* JOIN AS SOLICITOR */}
        {!user && (
          <Link to="/register-solicitor" className="navLink" onClick={handleNavClick}>
            <FiUserPlus /> <span>Join as Solicitor</span>
          </Link>
        )}

        {/* AUTH */}
        {!user && (
          <Link to="/login" className="loginBtn">
            Login
          </Link>
        )}

        {user && (
          <>
            <button className="dashboardBtn" onClick={goToDashboard}>
              Dashboard
            </button>

            <div className="profileWrapper" ref={dropdownRef}>
              <button className="profileBtn" onClick={() => setOpen(!open)}>
                <FiUser />
                <span>{user.name}</span>
                <FiChevronDown className={open ? "rotate" : ""} />
              </button>

              {open && (
                <div className="dropdown">
                  <button className="dropdownItem danger" onClick={handleLogout}>
                    <FiLogOut /> Logout
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </nav>
    </header>
  );
}